import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Info, Loader2, Wrench } from 'lucide-react'
import { buildDayLoad, fetchLabDayLoad } from '../../lib/lab.js'
import { TH_DAYS_SHORT as TH_DAYS, TH_MONTHS_SHORT as TH_MONTHS, bkkTodayISO, fmtDate } from '../../kit'

// กำหนดส่ง — a month calendar that shows how much lab work each day already carries, so a
// new piece can be put where there is room instead of on top of five others.
//
// 🔑 IT PICKS THE DEADLINE, NOT วันที่ (Ram, 2026-09-07). `lab.date` is the handover day
// and follows the linked นัดหมาย; `deadline` is the day a human chooses — when the piece
// must be at the clinic, and on the SSDC bench the day its daily quota is counted on.
// Which is why the workload sits under THIS field: it is the one that spends capacity.
//
// ⚠️ งานแก้ DOES NOT COUNT toward the number (Ram, 2026-09-07) — this reverses the
// 2026-08-18 rule. The figure exists to stand in for หัก Lab หมอ, the ค่า Lab deducted from
// a doctor's fee, and a remake carries no ค่า Lab. A day that holds remakes still gets the
// 🔧 mark and names them in its tooltip, so "3 ชิ้น 🔧" still reads as a day with rework on
// top — the bench-occupancy argument behind the old rule survives as the marker.
//
// ⚠️ THE DAY IS DECIDED BY `deadline` (falling back to `date` on the old imports that
// carry none), NEVER BY lab.year / lab.month. Those two columns belong to payroll:
// df_doctor/gen2 rewrites them to pull an unsettled in-house lab into the month it is
// generating, and touches neither date column. Filtering on them hid 99 of September
// 2026's 287 in-house pieces (measured 2026-09-07). See lib/lab.js §per-day workload
// and Xano api 648.
//
// SCOPED TO THE CHOSEN ผู้ผลิต Lab, because that is whose capacity is being spent. The
// clinic-wide number would be meaningless: a day with eight pieces at four different labs
// is not a busy day for any of them. With no vendor picked yet there is nothing to count
// against, and the picker says so instead of showing a number for the wrong thing.
//
// NO CEILING IS ENFORCED, and that is a decision rather than a gap. The in-house bench
// runs about ELEVEN pieces a day, but Ram wants that flexible, not a limit (2026-09-07):
// a day that needs twelve should take twelve, not be refused by software. So the shading
// stays RELATIVE to the busiest day in the month on screen, the raw count is always shown,
// and no day is ever disabled for being busy. If a hard cap is ever wanted, it belongs
// here as one named constant — not as a number sprinkled through the picker.
const BKK = 7 * 3600 * 1000
const pad = (n) => String(n).padStart(2, '0')
const key = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`

// `maxDay` ('YYYY-MM-DD') is the last selectable day — the linked appointment's, because a
// piece cannot be DUE after the visit it is handed over at (Ram, 2026-08-18, restated
// 2026-09-07). Earlier is the deliberate case, not the exception: work the clinic wants in
// hand before the patient arrives. Later days are shown with their counts but cannot be
// picked, so the workload beyond the visit is still visible while a new violation is
// simply unreachable. ISO date strings compare correctly as strings, which is why the key
// format is what it is.
export default function LabDayPicker({ value, onChange, lablistId, vendorName = '', maxDay = '', disabled = false }) {
  // The month on screen. Follows `value` when it is set, else today.
  const [cursor, setCursor] = useState(() => {
    const m = /^(\d{4})-(\d{2})/.exec(value || bkkTodayISO())
    return { year: Number(m[1]), month: Number(m[2]) }
  })
  const [load, setLoad] = useState(null)      // Map | null (loading)
  const [failed, setFailed] = useState(false)

  // One fetch per (vendor, month), remembered for the life of the form — staff step back
  // and forth across months while deciding, and each step is otherwise another round trip.
  const cache = useRef(new Map())
  const reqId = useRef(0)

  const vendor = Number(lablistId) || 0
  useEffect(() => {
    if (!vendor) { setLoad(null); return }
    const ck = `${vendor}-${cursor.year}-${cursor.month}`
    const hit = cache.current.get(ck)
    if (hit) { setLoad(hit); setFailed(false); return }
    const seq = ++reqId.current
    setLoad(null); setFailed(false)
    // The SAME UTC arithmetic the grid below is laid out with, shifted to Bangkok — so the
    // window asked for and the cells drawn can never disagree about where a month ends.
    fetchLabDayLoad({
      lablistId: vendor,
      from: Date.UTC(cursor.year, cursor.month - 1, 1) - BKK,
      to: Date.UTC(cursor.year, cursor.month, 1) - BKK,
    })
      .then((rows) => {
        if (seq !== reqId.current) return
        const map = buildDayLoad(rows)
        cache.current.set(ck, map)
        setLoad(map)
      })
      .catch(() => { if (seq === reqId.current) { setLoad(new Map()); setFailed(true) } })
  }, [vendor, cursor.year, cursor.month])

  const grid = useMemo(() => {
    const { year, month } = cursor
    // UTC arithmetic throughout: these are calendar cells, not instants, so the local
    // timezone must never get a vote in which day lands in which column.
    const first = new Date(Date.UTC(year, month - 1, 1))
    const lead = first.getUTCDay()
    const days = new Date(Date.UTC(year, month, 0)).getUTCDate()
    const cells = []
    for (let i = 0; i < lead; i++) cells.push(null)
    for (let d = 1; d <= days; d++) cells.push(key(year, month, d))
    return cells
  }, [cursor])

  // Shading tracks the NUMBER ON SCREEN (งานแก้-free), not the physical piece count, so a
  // dark cell and a big number always mean the same thing.
  const peak = useMemo(() => {
    let max = 0
    for (const k of grid) if (k) max = Math.max(max, load?.get(k)?.count || 0)
    return max
  }, [grid, load])

  const step = (delta) => setCursor(({ year, month }) => {
    const m = month + delta
    if (m < 1) return { year: year - 1, month: 12 }
    if (m > 12) return { year: year + 1, month: 1 }
    return { year, month: m }
  })

  const today = bkkTodayISO()

  if (!vendor) {
    return <div className="rounded-xl border border-dashed border-slate-200 px-3 py-2.5 text-[13px] text-slate-400">เลือกผู้ผลิต Lab ก่อน จึงจะดูงานในแต่ละวันได้</div>
  }

  return (
    <div className={`rounded-xl border border-slate-200 bg-white ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b border-slate-100">
        <button type="button" onClick={() => step(-1)} className="p-1 rounded text-slate-400 hover:text-brand-600 hover:bg-slate-50"><ChevronLeft size={15} /></button>
        <div className="text-[12.5px] font-semibold text-slate-700 tabular-nums">
          {TH_MONTHS[cursor.month - 1]} {cursor.year}
          {load === null && <Loader2 size={11} className="inline ml-1.5 animate-spin text-slate-400" />}
        </div>
        <button type="button" onClick={() => step(1)} className="p-1 rounded text-slate-400 hover:text-brand-600 hover:bg-slate-50"><ChevronRight size={15} /></button>
      </div>

      <div className="grid grid-cols-7 gap-px px-2 pt-1.5 text-center">
        {TH_DAYS.map((d) => <div key={d} className="text-[10.5px] text-slate-400 py-0.5">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 p-2 pt-1">
        {grid.map((k, i) => {
          if (!k) return <div key={`x${i}`} />
          const n = load?.get(k)
          const count = n?.count || 0        // NEW pieces — งานแก้ excluded, see the header
          const repair = n?.repair || 0
          const sel = k === value
          const isToday = k === today
          // The current value stays selectable-looking even if it violates the cap — a
          // legacy row dated past its appointment must still render as what it is.
          const tooLate = !!maxDay && k > maxDay && !sel
          // Relative shading: 0 stays plain, the busiest day is the darkest. No invented cap.
          const heat = peak > 0 ? count / peak : 0
          const bg = count === 0 ? '' : `rgba(79,70,229,${(0.07 + heat * 0.20).toFixed(3)})`
          return (
            <button
              key={k}
              type="button"
              onClick={() => !tooLate && onChange(k)}
              disabled={tooLate}
              title={tooLate
                ? 'หลังวันนัดหมาย — เลือกไม่ได้'
                : (count || repair)
                    ? `${count} ชิ้น${repair ? ` · งานแก้อีก ${repair} (ไม่นับรวม)` : ''}`
                    : 'ยังไม่มีงาน'}
              style={sel || tooLate ? undefined : { backgroundColor: bg }}
              className={`relative rounded-md py-1 text-center transition ${
                sel ? 'bg-brand-600 text-white shadow-sm'
                  : tooLate ? 'opacity-35 cursor-not-allowed'
                    : `hover:ring-1 hover:ring-brand-300 ${isToday ? 'ring-1 ring-slate-300' : ''}`
              }`}
            >
              <div className={`text-[12px] tabular-nums leading-none ${sel ? 'text-white' : 'text-slate-700'}`}>{Number(k.slice(8))}</div>
              <div className={`text-[10px] leading-tight mt-0.5 tabular-nums ${sel ? 'text-white/90' : count ? 'text-slate-500' : 'text-slate-300'}`}>
                {count || '—'}
              </div>
              {/* งานแก้ marked but NOT added in — a day showing "0 🔧" is a day of pure
                  rework, which is exactly the distinction the number is meant to lose. */}
              {repair > 0 && (
                <span className={`absolute top-0.5 right-0.5 ${sel ? 'text-white/90' : 'text-amber-500'}`}><Wrench size={8} /></span>
              )}
            </button>
          )
        })}
      </div>

      <div className="px-2.5 pb-2 space-y-1">
        <div className="flex items-center gap-1.5 text-[10.5px] text-slate-400">
          <Info size={10} className="shrink-0" />
          <span>ตัวเลข = งานของ{vendorName ? ` ${vendorName}` : 'แล็บนี้'}ในวันนั้น · <Wrench size={8} className="inline text-amber-500" /> มีงานแก้ (ไม่นับรวม)</span>
        </div>
        {maxDay && <div className="text-[10.5px] text-slate-400">เลือกได้ถึง {fmtDate(maxDay)} (วันนัดหมาย) — หลังจากนั้นเลือกไม่ได้</div>}
        {/* Never presented as a capacity figure, because it cannot be one. */}
        <div className="text-[10.5px] text-slate-400">
          {failed ? 'โหลดจำนวนงานไม่สำเร็จ — เลือกกำหนดส่งได้ตามปกติ'
            : 'นับตามกำหนดส่งของงาน (ไม่อิงเดือน/ปีที่ใช้หักเงินเดือนหมอ) · ไม่รวมงานแก้ งานที่ยังไม่ผูกกับการรักษา และงานที่ถูกลบแล้ว'}
        </div>
      </div>
    </div>
  )
}
